import { FC, useCallback, useRef } from 'react';
import { Table } from '@silverfort/sf-components';
import { useColumns, useEmptyState } from './hooks';
import { Application } from '../../../types/Application';
import { useRowMenuItems } from './hooks/useRowMenuItems';
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from './consts';
import { useApplicationsContext } from './context/ApplicationsContext';
import { SortDirection } from '@silverfort/sf-components/dist/Table/interface';
import useGetRefSize from '../../../../../hooks/useGetRefSize';
import { useHistory } from 'react-router-dom';
import { useActiveDirectoryAccessContext } from '../../../context';
import { useDeleteApplicationContext } from './context/DeleteApplicationContext';

export const ApplicationsTable: FC = () => {
    const { hasAccessIntelligenceEditorRole } = useActiveDirectoryAccessContext();
    const {
        applications,
        applicationsLoading,
        fetchMoreApplications,
        filtersApplied,
        setSorting,
        setShowUploadApplicationsModal
    } = useApplicationsContext();
    const { deleteApplicationLoading, setApplicationToDelete } = useDeleteApplicationContext();
    const history = useHistory();

    const navigateToApplicationPage = useCallback(
        (appId: string) => {
            history.push(`/accessAnalysis/Application/${appId}`);
        },
        [history]
    );

    const navigateToCreateApplicationPage = useCallback(
        (app?: Application) => {
            if (app) {
                history.push('/accessAnalysis/Create', {
                    application: { ...app },
                    editMode: true
                });
            } else {
                history.push('/accessAnalysis/Create');
            }
        },
        [history]
    );

    const openUploadApplicationModal = useCallback(() => {
        setShowUploadApplicationsModal(true);
    }, [setShowUploadApplicationsModal]);

    const updateSorting = useCallback(
        (field: keyof Application, direction: SortDirection) => {
            if (applications.length === 0) return undefined;

            setSorting({ direction, field });
        },
        [applications, setSorting]
    );

    const ref = useRef<HTMLDivElement | null>(null);
    const { height } = useGetRefSize(ref);

    const columns = useColumns(navigateToApplicationPage);
    const emptyStateProps = useEmptyState(
        filtersApplied,
        navigateToCreateApplicationPage,
        openUploadApplicationModal
    );
    const menuItems = useRowMenuItems(
        deleteApplicationLoading,
        hasAccessIntelligenceEditorRole,
        setApplicationToDelete,
        navigateToCreateApplicationPage
    );

    return (
        <div className="h-full flex w-full" ref={ref}>
            <Table
                EmptyStateProps={emptyStateProps}
                bodyHeight={height ? height - 58 : 0}
                columns={columns}
                defaultSortColumn={DEFAULT_SORT_FIELD}
                defaultSortDirection={DEFAULT_SORT_DIRECTION}
                loading={applicationsLoading}
                menuItems={menuItems}
                onScrollEnd={fetchMoreApplications}
                onSortChange={updateSorting}
                rows={applications}
                tableContainerClassName="w-full h-full flex flex-col"
            />
        </div>
    );
};
